import React, { useState } from 'react';
import theme from '../../theme/globalTheme';
import { Card, Upload, Alert, Typography, List, Tag, Divider, Button, Tabs } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, DownloadOutlined, ToolOutlined } from '@ant-design/icons';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

function SeabobDocumentValidator() {
  const [validationResults, setValidationResults] = useState({
    contrat: null,
    confirmation: null,
    facturation: null
  });
  const [loading, setLoading] = useState(false);
  const [originalZips, setOriginalZips] = useState({});
  const [originalFileNames, setOriginalFileNames] = useState({});

  // Variables pour chaque type de document Seabob
  const seabobVariables = {
    contrat: [
      'charterer_date',
      'departure_hour',
      'returning_hour',
      'client_name',
      'seabob_number',
      'seabob_duration',
      'seabob_price',
      'e_surf_number',
      'e_surf_duration',
      'e_surf_price',
      'additional_row',
      'addtional_price',
      'total_price',
      'spot'
    ],
    confirmation: [
      'today_date',
      'charter_date',
      'client_name',
      'seabob_number',
      'seabob_duration',
      'seabob_price',
      'e_surf_number',
      'e_surf_duration',
      'e_surf_price',
      'additional_row',
      'addtional_price',
      'total_price'
    ],
    facturation: [
      'invoice_number',
      'charterer',
      'charter_date',
      'client_name',
      'today_date',
      'seabob_number',
      'seabob_duration',
      'seabob_price',
      'e_surf_number',
      'e_surf_duration',
      'e_surf_price',
      'additional_row',
      'addtional_price',
      'total_price'
    ]
  };

  const debugDocumentContent = (documentXml) => {
    // Chercher tous les textes entre balises w:t
    const textRegex = /<w:t[^>]*>([^<]+)<\/w:t>/g;
    const texts = [];
    let textMatch;
    
    while ((textMatch = textRegex.exec(documentXml)) !== null) {
      texts.push(textMatch[1]);
    }
    
    console.log('📝 Textes trouvés dans le document:', texts);
    return texts;
  };

  const validateTemplate = async (file, documentType) => {
    setLoading(true);

    try {
      // Lire le fichier Word
      const arrayBuffer = await file.arrayBuffer();
      const zip = new PizZip(arrayBuffer);

      // Vérifier que le fichier est bien un DOCX valide
      const files = zip.file(/.*/);
      console.log('📁 Fichiers dans le ZIP:', files.map(f => f.name));
      
      // Vérifier que document.xml existe
      if (!zip.file('word/document.xml')) {
        throw new Error('Fichier DOCX invalide - document.xml manquant');
      }

      // Sauvegarder le ZIP original pour la correction
      setOriginalZips(prev => ({ ...prev, [documentType]: zip }));
      setOriginalFileNames(prev => ({ ...prev, [documentType]: file.name }));

      // Variables attendues pour ce type de document
      const expectedVariables = seabobVariables[documentType];

      // Extraire le XML du document
      const documentXml = zip.file('word/document.xml').asText();

      // DEBUG: Afficher un extrait du XML pour voir le format réel
      console.log('🔍 Extrait XML:', documentXml.substring(0, 1000));
      
      // DEBUG: Afficher tous les textes du document
      const allTexts = debugDocumentContent(documentXml);

      // Regex améliorée pour capturer différents formats de variables
      // Permet les lettres, chiffres, underscores ET tirets dans les noms de variables
      const variableRegex = /\{\{([\w-]+)\}\}|\[\[([\w-]+)\]\]|\$([\w-]+)|\{([\w-]+)\}/g;
      const foundVariables = [];
      let match;

      while ((match = variableRegex.exec(documentXml)) !== null) {
        // Prendre le premier groupe capturé non-undefined
        const varName = match.slice(1).find(group => group !== undefined);
        if (varName && !foundVariables.includes(varName)) {
          foundVariables.push(varName);
        }
      }

      console.log('📋 Variables trouvées:', foundVariables);
      console.log('📋 Variables attendues:', expectedVariables);

      // Détecter les variables cassées (avec des balises XML au milieu)
      const brokenVariableRegex = /\[\[[^\]]*?<w:[^>]+>[^\]]*?\]\]/g;
      const brokenMatches = documentXml.match(brokenVariableRegex) || [];
      const hasBrokenVariables = brokenMatches.length > 0;

      // Détecter les crochets non fermés
      const unclosedBrackets = (documentXml.match(/\[\[[^\]]*?(?!\]\])/g) || []).length;

      // Comparer avec les variables attendues
      const missingVariables = expectedVariables.filter(v => !foundVariables.includes(v));
      const extraVariables = foundVariables.filter(v => !expectedVariables.includes(v));
      const validVariables = foundVariables.filter(v => expectedVariables.includes(v));

      // Déterminer le statut global
      const isValid = missingVariables.length === 0 &&
                      extraVariables.length === 0 &&
                      !hasBrokenVariables &&
                      unclosedBrackets === 0;

      setValidationResults(prev => ({
        ...prev,
        [documentType]: {
          isValid,
          hasBrokenVariables,
          brokenCount: brokenMatches.length,
          unclosedBrackets,
          foundVariables,
          validVariables,
          missingVariables,
          extraVariables,
          expectedVariables,
          fileName: file.name
        }
      }));

    } catch (error) {
      console.error('Erreur validation:', error);
      setValidationResults(prev => ({
        ...prev,
        [documentType]: {
          isValid: false,
          error: `Erreur lors de la validation: ${error.message}`
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (file, documentType) => {
    if (file.name.endsWith('.docx')) {
      validateTemplate(file, documentType);
    } else {
      setValidationResults(prev => ({
        ...prev,
        [documentType]: {
          isValid: false,
          error: 'Veuillez sélectionner un fichier .docx'
        }
      }));
    }
    return false; // Empêcher l'upload automatique
  };

  const fixTemplate = async (documentType) => {
    const originalZip = originalZips[documentType];
    const validationResult = validationResults[documentType];

    if (!originalZip || !validationResult) {
      return;
    }

    setLoading(true);
    try {
      let documentXml = originalZip.file('word/document.xml').asText();

      console.log('🔧 Correction du template en cours...');

      // ÉTAPE 1: Normaliser toutes les variables au format [[nom_variable]]
      // Chercher différents formats et les standardiser
      const formatsToReplace = [
        { regex: /\{\{(\w+)\}\}/g, replacement: '[[$1]]' },
        { regex: /\$(\w+)/g, replacement: '[[$1]]' },
        { regex: /\{(\w+)\}/g, replacement: '[[$1]]' }
      ];

      formatsToReplace.forEach(format => {
        const matches = documentXml.match(format.regex);
        if (matches) {
          console.log(`🔄 Normalisation format: ${matches.join(', ')}`);
        }
        documentXml = documentXml.replace(format.regex, format.replacement);
      });

      // ÉTAPE 2: Nettoyer les variables cassées avec balises XML
      const brokenVariableRegex = /\[\[([^\]]*?(?:<w:[^>]+>)+[^\]]*?)\]\]/g;
      documentXml = documentXml.replace(brokenVariableRegex, (match, content) => {
        // Extraire uniquement le texte, supprimer toutes les balises XML
        const cleanText = content.replace(/<w:[^>]+>/g, '').replace(/<\/w:[^>]+>/g, '');
        console.log(`  Correction variable cassée: ${match} → [[${cleanText}]]`);
        return `[[${cleanText}]]`;
      });

      // ÉTAPE 3: S'assurer que toutes les variables attendues sont présentes
      const expectedVariables = seabobVariables[documentType];
      const existingVariables = [];
      const variableCheckRegex = /\[\[(\w+)\]\]/g;
      let variableMatch;

      while ((variableMatch = variableCheckRegex.exec(documentXml)) !== null) {
        existingVariables.push(variableMatch[1]);
      }

      const missingVariables = expectedVariables.filter(v => !existingVariables.includes(v));

      // ÉTAPE 4: Ajouter les variables manquantes
      if (missingVariables.length > 0) {
        console.log('📝 Ajout des variables manquantes:', missingVariables);
        
        // Trouver la dernière position avant </w:body>
        const bodyCloseIndex = documentXml.lastIndexOf('</w:body>');
        if (bodyCloseIndex !== -1) {
          let missingVarsXml = '\n<w:p><w:r><w:t>Variables manquantes ajoutées automatiquement:</w:t></w:r></w:p>\n';
          
          missingVariables.forEach(varName => {
            missingVarsXml += `<w:p><w:r><w:t>[[${varName}]]</w:t></w:r></w:p>\n`;
          });
          
          missingVarsXml += '<w:p><w:r><w:t></w:t></w:r></w:p>\n';
          
          documentXml = documentXml.slice(0, bodyCloseIndex) + missingVarsXml + documentXml.slice(bodyCloseIndex);
        }
      }

      // ÉTAPE 5: Sauvegarder le document corrigé
      originalZip.file('word/document.xml', documentXml);

      const blob = originalZip.generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });

      const fixedFileName = originalFileNames[documentType].replace('.docx', '_CORRIGE.docx');
      saveAs(blob, fixedFileName);

      console.log('✅ Template corrigé téléchargé:', fixedFileName);

      // Re-valider le document corrigé
      const correctedFile = new File([blob], fixedFileName, { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      await validateTemplate(correctedFile, documentType);

    } catch (error) {
      console.error('❌ Erreur correction:', error);
      alert('Erreur lors de la correction: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderValidationResult = (documentType) => {
    const validationResult = validationResults[documentType];

    if (!validationResult) return null;

    if (validationResult.error) {
      return (
        <Alert
          message="Erreur"
          description={validationResult.error}
          type="error"
          showIcon
          className="mt-4"
        />
      );
    }

    return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark, padding: '20px' }} className="mt-6">
        <Divider />

        {/* Info sur le fichier */}
        <Alert
          message={`📄 Document: ${validationResult.fileName}`}
          description={
            <div>
              <p className="mb-1">
                <strong>Type:</strong> {documentType.charAt(0).toUpperCase() + documentType.slice(1)}
              </p>
              <p className="mb-0">
                <strong>Variables trouvées:</strong> {validationResult.foundVariables?.length || 0} / {validationResult.expectedVariables?.length || 0}
              </p>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        <Alert
          message={validationResult.isValid ? '✅ Template VALIDE' : '❌ Template INVALIDE'}
          description={
            validationResult.isValid
              ? 'Toutes les variables sont correctement formatées. Ce template peut être uploadé dans Supabase.'
              : 'Des problèmes ont été détectés dans ce template. Corrigez-les avant de l\'utiliser.'
          }
          type={validationResult.isValid ? 'success' : 'error'}
          showIcon
          icon={validationResult.isValid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          className="mb-4"
        />

        {/* Bouton de correction automatique */}
        {!validationResult.isValid && !validationResult.fixed && (
          <div className="mb-4">
            <Button
              type="primary"
              size="large"
              icon={<ToolOutlined />}
              onClick={() => fixTemplate(documentType)}
              loading={loading}
              block
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              🔧 Corriger automatiquement et télécharger
            </Button>
            <Paragraph type="secondary" className="mt-2 mb-0">
              Cette fonction va nettoyer toutes les variables cassées et ajouter les variables manquantes à la fin du document.
            </Paragraph>
          </div>
        )}

        {/* Message de succès après correction */}
        {validationResult.fixed && (
          <Alert
            message="✅ Template corrigé téléchargé !"
            description={
              <div>
                <Paragraph className="mb-2">
                  Le fichier <Text code>{originalFileNames[documentType]?.replace('.docx', '_CORRIGE.docx')}</Text> a été téléchargé avec succès.
                </Paragraph>
                <Paragraph className="mb-2">
                  <Text strong>Prochaines étapes :</Text>
                </Paragraph>
                <ol style={{ paddingLeft: '20px', marginBottom: 0 }}>
                  <li>Ouvrez le fichier téléchargé dans Microsoft Word</li>
                  <li>Vérifiez que toutes les variables sont correctement placées</li>
                  <li>Ajustez la mise en forme si nécessaire</li>
                  <li>Uploadez le fichier dans Supabase Storage {">"} templates avec le nom: <Text code>seabob-{documentType}-template.docx</Text></li>
                </ol>
              </div>
            }
            type="success"
            showIcon
            icon={<DownloadOutlined />}
            className="mb-4"
          />
        )}

        {validationResult.hasBrokenVariables && (
          <Alert
            message={`⚠️ ${validationResult.brokenCount} variable(s) cassée(s) détectée(s)`}
            description="Des balises XML Word sont présentes DANS les variables [[]]. Cela arrive quand vous appliquez du formatage pendant que vous tapez la variable."
            type="error"
            showIcon
            icon={<WarningOutlined />}
            className="mb-4"
          />
        )}

        {validationResult.unclosedBrackets > 0 && (
          <Alert
            message={`⚠️ ${validationResult.unclosedBrackets} crochet(s) non fermé(s)`}
            description="Certaines variables ont [[ sans ]] correspondant."
            type="warning"
            showIcon
            className="mb-4"
          />
        )}

        <Card size="small" className="mb-4" title={`✅ Variables valides trouvées (${validationResult.validVariables?.length || 0}/${validationResult.expectedVariables?.length || 0})`}>
          {(validationResult.validVariables?.length || 0) > 0 ? (
            <div>
              {validationResult.validVariables.map(v => (
                <Tag color="green" key={v} className="mb-2">
                  {`[[${v}]]`}
                </Tag>
              ))}
            </div>
          ) : (
            <Text type="secondary">Aucune variable valide trouvée</Text>
          )}
        </Card>

        {(validationResult.missingVariables?.length || 0) > 0 && (
          <Card size="small" className="mb-4" title={`❌ Variables manquantes (${validationResult.missingVariables?.length || 0})`} style={{ borderColor: '#ff4d4f' }}>
            <Paragraph>
              Ces variables sont attendues mais absentes du template :
            </Paragraph>
            {validationResult.missingVariables.map(v => (
              <Tag color="red" key={v} className="mb-2">
                {`[[${v}]]`}
              </Tag>
            ))}
          </Card>
        )}

        {(validationResult.extraVariables?.length || 0) > 0 && (
          <Card size="small" className="mb-4" title={`⚠️ Variables inconnues (${validationResult.extraVariables?.length || 0})`} style={{ borderColor: '#faad14' }}>
            <Paragraph>
              Ces variables sont dans le template mais ne seront pas remplies :
            </Paragraph>
            {validationResult.extraVariables.map(v => (
              <Tag color="orange" key={v} className="mb-2">
                {`[[${v}]]`}
              </Tag>
            ))}
          </Card>
        )}

        <Divider />

        <Title level={4}>🔧 Comment corriger :</Title>
        <List
          size="small"
          dataSource={[
            {
              title: 'Variables cassées par le formatage',
              description: 'Supprimez la variable et retapez-la EN UNE SEULE FOIS. Appliquez le formatage APRÈS.',
              show: validationResult.hasBrokenVariables
            },
            {
              title: 'Variables manquantes',
              description: 'Ajoutez les variables manquantes listées ci-dessus.',
              show: (validationResult.missingVariables?.length || 0) > 0
            },
            {
              title: 'Variables inconnues',
              description: 'Vérifiez l\'orthographe ou supprimez-les si inutilisées.',
              show: (validationResult.extraVariables?.length || 0) > 0
            }
          ].filter(item => item.show)}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={<Text strong>{item.title}</Text>}
                description={item.description}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  return (
    <div className="p-6">
      <Card>
        <Title level={2}>🔍 Validateur de Templates Seabob</Title>

        <Alert
          message="Vérifiez si vos templates Seabob ont des variables cassées"
          description="Uploadez vos 3 templates (Contrat, Confirmation, Facturation) pour détecter les problèmes de formatage avant de les utiliser."
          type="info"
          showIcon
          className="mb-6"
        />

        <Tabs defaultActiveKey="contrat">
          <TabPane tab="📄 Contrat" key="contrat">
            <Alert
              message="Variables attendues pour le Contrat"
              description={
                <div>
                  <Text>Le template de Contrat Seabob doit contenir ces variables :</Text>
                  <div style={{ marginTop: '8px' }}>
                    {seabobVariables.contrat.map(v => (
                      <Tag key={v} style={{ marginBottom: '4px' }}>{`[[${v}]]`}</Tag>
                    ))}
                  </div>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Upload.Dragger
              accept=".docx"
              beforeUpload={(file) => handleUpload(file, 'contrat')}
              showUploadList={false}
              disabled={loading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Uploadez seabob-contrat-template.docx</p>
              <p className="ant-upload-hint">
                Cliquez ou glissez votre template de Contrat ici
              </p>
            </Upload.Dragger>

            {renderValidationResult('contrat')}
          </TabPane>

          <TabPane tab="✅ Confirmation" key="confirmation">
            <Alert
              message="Variables attendues pour la Confirmation"
              description={
                <div>
                  <Text>Le template de Confirmation Seabob doit contenir ces variables :</Text>
                  <div style={{ marginTop: '8px' }}>
                    {seabobVariables.confirmation.map(v => (
                      <Tag key={v} style={{ marginBottom: '4px' }}>{`[[${v}]]`}</Tag>
                    ))}
                  </div>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Upload.Dragger
              accept=".docx"
              beforeUpload={(file) => handleUpload(file, 'confirmation')}
              showUploadList={false}
              disabled={loading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Uploadez seabob-confirmation-template.docx</p>
              <p className="ant-upload-hint">
                Cliquez ou glissez votre template de Confirmation ici
              </p>
            </Upload.Dragger>

            {renderValidationResult('confirmation')}
          </TabPane>

          <TabPane tab="💰 Facturation" key="facturation">
            <Alert
              message="Variables attendues pour la Facturation"
              description={
                <div>
                  <Text>Le template de Facturation Seabob doit contenir ces variables :</Text>
                  <div style={{ marginTop: '8px' }}>
                    {seabobVariables.facturation.map(v => (
                      <Tag key={v} style={{ marginBottom: '4px' }}>{`[[${v}]]`}</Tag>
                    ))}
                  </div>
                </div>
              }
              type="info"
              showIcon
              className="mb-4"
            />

            <Upload.Dragger
              accept=".docx"
              beforeUpload={(file) => handleUpload(file, 'facturation')}
              showUploadList={false}
              disabled={loading}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Uploadez seabob-facturation-template.docx</p>
              <p className="ant-upload-hint">
                Cliquez ou glissez votre template de Facturation ici
              </p>
            </Upload.Dragger>

            {renderValidationResult('facturation')}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
}

export default SeabobDocumentValidator;